import { useState, useEffect, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { abreWhatsApp } from "@/lib/abreWhatsApp";
import { Copy, Check, ExternalLink, QrCode, Download, Share2, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import QRCode from "qrcode";

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export type PublicLinkCardProps = {
  url: string;
  label: string;
  icon: ReactNode;
  shareLabel: string;
  className?: string;
};

function slugifyForFilename(label: string): string {
  return (
    label
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "") || "qrcode"
  );
}

export function PublicLinkCard({ url, label, icon, shareLabel, className }: PublicLinkCardProps) {
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);

  const displayUrl = (u: string) => u.replace(/^https?:\/\//, "");

  // Detecta suporte a Web Share API com arquivos (varia muito: iOS Safari OK,
  // Chrome Android OK, desktop quase nunca). Pra ambientes sem suporte mostra
  // só o botao Baixar.
  const canShareFiles =
    typeof navigator !== "undefined" &&
    typeof (navigator as Navigator).share === "function" &&
    typeof (navigator as Navigator).canShare === "function";

  useEffect(() => {
    if (!qrOpen || !url) return;
    setQrError(null);
    // PNG (margin 2 quiet zone, 512px) — universal pra scanners, ~2KB.
    // Pre-renderiza no canvas escondido pra baixar com a mesma resolução
    // que aparece no dialog (alta resolução pra impressão).
    QRCode.toDataURL(url, { width: 512, margin: 2, errorCorrectionLevel: "M" })
      .then((data) => setQrDataUrl(data))
      .catch((err) => {
        setQrError(err instanceof Error ? err.message : "Falha ao gerar QR Code");
      });
  }, [qrOpen, url]);

  const handleCopy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWhatsAppShare = () => {
    if (!url) return;
    const message = `Confira ${shareLabel}: ${url}`;
    abreWhatsApp(`https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`);
  };

  const downloadQr = () => {
    if (!qrDataUrl) return;
    const a = document.createElement("a");
    a.href = qrDataUrl;
    a.download = `qrcode-${slugifyForFilename(label)}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const shareQr = async () => {
    if (!qrDataUrl || !canShareFiles) return;
    setSharing(true);
    try {
      const blob = await fetch(qrDataUrl).then((r) => r.blob());
      const file = new File([blob], `qrcode-${slugifyForFilename(label)}.png`, {
        type: "image/png",
      });
      const shareData: ShareData = {
        files: [file],
        title: `QR Code — ${label}`,
        text: url,
      };
      if (!navigator.canShare(shareData)) {
        downloadQr();
        return;
      }
      await navigator.share(shareData);
    } catch (err) {
      // AbortError = user cancelou, ignora silencioso.
      if ((err as Error)?.name !== "AbortError") {
        downloadQr();
      }
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className={`flex flex-col gap-2 ${className || ""}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-semibold text-secondary">{label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0 cursor-pointer border-primary/30 text-primary hover:bg-primary/10"
            onClick={handleCopy}
            title="Copiar"
          >
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
          </Button>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 cursor-pointer border-primary/30 text-primary hover:bg-primary/10"
              title="Abrir"
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          </a>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0 cursor-pointer border-primary/30 text-primary hover:bg-primary/10"
            onClick={() => setQrOpen(true)}
            title="QR Code"
          >
            <QrCode className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0 cursor-pointer text-[#25D366] border-[#25D366]/40 hover:bg-[#25D366]/10"
            onClick={handleWhatsAppShare}
            title="Compartilhar via WhatsApp"
          >
            <WhatsAppIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <Input
        value={displayUrl(url)}
        readOnly
        className="h-9 text-xs font-mono w-full bg-background border-primary/30"
        onClick={(e) => (e.target as HTMLInputElement).select()}
      />
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>QR Code — {label}</DialogTitle>
            <DialogDescription className="text-xs font-mono break-all">
              {displayUrl(url)}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            <div className="w-64 h-64 rounded-lg border bg-white p-3 flex items-center justify-center">
              {qrError ? (
                <p className="text-xs text-destructive text-center">{qrError}</p>
              ) : qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt={`QR Code de ${label}`}
                  className="w-full h-full object-contain"
                />
              ) : (
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              )}
            </div>
            <div className="flex gap-2 w-full">
              <Button
                onClick={downloadQr}
                disabled={!qrDataUrl}
                className="flex-1 cursor-pointer"
              >
                <Download className="w-4 h-4 mr-2" /> Baixar PNG
              </Button>
              {canShareFiles && (
                <Button
                  onClick={shareQr}
                  disabled={!qrDataUrl || sharing}
                  variant="outline"
                  className="flex-1 cursor-pointer"
                >
                  {sharing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Share2 className="w-4 h-4 mr-2" /> Compartilhar
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
